/**
 * 标签系统API服务
 */

import type {
  Tag,
  TagWithWeight,
  WorkspaceTag,
  TagCreateRequest,
  WorkspaceTagsUpdateRequest,
  WorkspaceTagsResponse,
} from '../types/tag';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * 标签API服务类
 */
export class TagApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * 获取所有标签
   */
  async listTags(params?: {
    category?: string;
    subcategory?: string;
  }): Promise<Tag[]> {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append('category', params.category);
    if (params?.subcategory) queryParams.append('subcategory', params.subcategory);

    const response = await fetch(
      `${this.baseUrl}/api/tags?${queryParams.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );

    if (!response.ok) {
      throw new Error(`获取标签列表失败: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * 根据流派获取适用标签
   */
  async getTagsByGenre(
    category: 'male' | 'female',
    genre: string
  ): Promise<TagWithWeight[]> {
    const response = await fetch(
      `${this.baseUrl}/api/tags/genres/${category}/${encodeURIComponent(genre)}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );

    if (!response.ok) {
      throw new Error(`获取标签失败: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * 创建自定义标签
   */
  async createCustomTag(request: TagCreateRequest): Promise<Tag> {
    const response = await fetch(`${this.baseUrl}/api/tags/custom`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '创建标签失败');
    }

    return await response.json();
  }

  /**
   * 获取工作区标签
   */
  async getWorkspaceTags(workspaceId: number): Promise<WorkspaceTag[]> {
    const response = await fetch(
      `${this.baseUrl}/api/tags/workspaces/${workspaceId}`,
      {
        method: 'GET',
        credentials: 'include',
      }
    );

    if (!response.ok) {
      throw new Error(`获取工作区标签失败: ${response.statusText}`);
    }

    const data = await response.json();
    return data.tags;
  }

  /**
   * 更新工作区标签
   */
  async updateWorkspaceTags(
    workspaceId: number,
    tagIds: number[]
  ): Promise<WorkspaceTag[]> {
    const response = await fetch(
      `${this.baseUrl}/api/tags/workspaces/${workspaceId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ tag_ids: tagIds }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '更新工作区标签失败');
    }

    const data = await response.json();
    return data.tags;
  }
}

// 导出单例
export const tagApi = new TagApiService();
export default tagApi;
